<script lang="ts">
  // Bottom-edge chrome — interaction hint on the left, legal links +
  // version stamp on the right. Both rows are independently fixed at
  // the same `bottom` offset so their baselines align across the
  // viewport.
  //
  // Version is read from the @atlasguess/web package.json so we have
  // ONE source of truth. Bump the version there and this updates.
  //
  // Legal links live here (not the sidebar) so they're shared with
  // the PageShell-using static routes via the same chrome strip and
  // don't need a second copy in the sidebar.
  import { Link } from 'svelte-routing';
  import pkg from '../../package.json' with { type: 'json' };
  import { DONATIONS_ENABLED } from '../lib/features';

  interface Props {
    /** Sidebar expanded state — controls the hint's left offset so it
     *  tracks the visible main-area edge as the sidebar slides. */
    sidebarExpanded: boolean;
  }

  let { sidebarExpanded }: Props = $props();

  const COPYRIGHT_YEAR = new Date().getFullYear();
</script>

<div
  class="app-footer__hint"
  class:app-footer__hint--sidebar-collapsed={!sidebarExpanded}
  aria-hidden="true"
>
  Drag to pan &middot;
  <!-- Desktop and touch get different zoom verbs. Pure CSS swap via
       the same 768px breakpoint the rest of the app uses. -->
  <span class="app-footer__hint-pointer">Scroll to zoom</span>
  <span class="app-footer__hint-touch">Pinch to zoom</span>
</div>

<!-- Right-side meta strip: legal links + version, separated by middots.
     Same font/size/color as the version stamp so the row reads as one
     piece of chrome. The container is pointer-events:none so it never
     intercepts globe drags; individual links opt back in via CSS. -->
<div class="app-footer__meta">
  <Link to="/privacy" getProps={() => ({ class: 'app-footer__legal-link' })}>Privacy</Link>
  <span class="app-footer__sep" aria-hidden="true">&middot;</span>
  <Link to="/terms" getProps={() => ({ class: 'app-footer__legal-link' })}>Terms</Link>
  <span class="app-footer__sep" aria-hidden="true">&middot;</span>
  <Link to="/contact" getProps={() => ({ class: 'app-footer__legal-link' })}>Contact</Link>
  <span class="app-footer__sep" aria-hidden="true">&middot;</span>
  {#if DONATIONS_ENABLED}
    <Link to="/donate" getProps={() => ({ class: 'app-footer__legal-link' })}>Donate</Link>
    <span class="app-footer__sep" aria-hidden="true">&middot;</span>
  {/if}
  <!-- /discord is a Cloudflare Pages 302 to discord.gg. Keeping the
       visible href on our own domain dodges Chrome 142+'s Local Network
       Access prompt that fires when third-party links pre-detect
       installed desktop apps via 127.0.0.1 (Discord, Slack, Spotify…). -->
  <a href="/discord" target="_blank" rel="noopener noreferrer" class="app-footer__legal-link">
    Discord
  </a>
  <span class="app-footer__sep" aria-hidden="true">&middot;</span>
  <span class="app-footer__version-text">AtlasGuess {pkg.version} &copy; {COPYRIGHT_YEAR}</span>
</div>
